import { AnimatePresence, motion } from 'framer-motion';
import { Plus, Ticket } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { h4, p, textStyle } from '../../styles/typography';
import IconTextButton from '../ui/IconTextButton';
import Card from '../ui/card';
import ApplyCouponOverlay from './ApplyCouponOverlay';
import './HyperLocalReferralTracker.css';
import NewReferralOverlay from './NewReferralOverlay';
import ReferralDetails from './ReferralDetails';
import ReferralList from './ReferralList';
import { getAllReferrals, getInfluencerDetails } from './referralTrackerApi';

const HyperLocalReferralTracker = () => {
  // Log screen view
  useScreenLogging('HyperLocalReferralTracker', {
    component: 'HyperLocalReferralTracker',
    screenType: 'referral-tracker',
  });
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showNewReferralOverlay, setShowNewReferralOverlay] = useState(false);
  const [editingInfluencer, setEditingInfluencer] = useState(null);
  const [showCouponOverlay, setShowCouponOverlay] = useState(false);
  const [selectedReferralForCoupon, setSelectedReferralForCoupon] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const referralsData = await getAllReferrals();
      console.log('[HyperLocalReferralTracker] getAllReferrals response:', referralsData);
      setReferrals(referralsData);
    } catch (err) {
      console.error('Error loading referral data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectInfluencer = useCallback(async (item) => {
    setSelectedInfluencer(null);
    setDetailsLoading(true);
    try {
      const details = await getInfluencerDetails(item.influencer_id);
      console.log('[HyperLocalReferralTracker] getInfluencerDetails response:', details);
      setSelectedInfluencer(details || item);
    } catch (err) {
      console.error('Error loading influencer details:', err);
      setSelectedInfluencer(item);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const handleCloseDetails = useCallback(() => {
    setSelectedInfluencer(null);
  }, []);

  const showOverlay = selectedInfluencer !== null;
  const showCreateOverlay = showNewReferralOverlay;

  const handleCloseNewReferral = useCallback(() => {
    setShowNewReferralOverlay(false);
    setEditingInfluencer(null);
  }, []);

  const handleEditInfluencer = useCallback((influencer) => {
    setSelectedInfluencer(null);
    setEditingInfluencer(influencer);
    setShowNewReferralOverlay(true);
  }, []);

  const handleNewReferralSuccess = useCallback(() => {
    loadData();
  }, [loadData]);

  const handleApplyCoupon = useCallback((referral) => {
    setSelectedReferralForCoupon(referral);
    setShowCouponOverlay(true);
  }, []);

  const handleCloseCouponOverlay = useCallback(() => {
    setShowCouponOverlay(false);
    setSelectedReferralForCoupon(null);
  }, []);

  const handleCouponApply = useCallback(async (couponCode, referral, personName) => {
    // This is where you would integrate with your coupon/discount API
    // The referral code has been created/retrieved and can now be used as a coupon code
    console.log('Coupon code ready to use:', couponCode, 'for referral:', referral, 'applied by:', personName);
    // You can add API call here to apply the coupon in billing system
    // Example: await applyCouponCode(couponCode, referral, personName);
    return Promise.resolve();
  }, []);

  const handleCodeCreated = useCallback(async (code, referral, personName, attributionResult) => {
    // Refresh only the specific referral that had the code applied, not all referrals
    console.log('Referral code applied:', code, 'for referral:', referral, 'applied by:', personName);
    
    const influencerId = referral?.influencer_id || referral?.id;
    if (!influencerId) {
      console.warn('No influencer_id found, skipping individual refresh');
      return;
    }

    try {
      // Get updated details for only this specific influencer
      const updatedDetails = await getInfluencerDetails(influencerId);
      
      if (updatedDetails) {
        // Update only this referral in the referrals array
        setReferrals(prevReferrals => 
          prevReferrals.map(r => {
            const rId = r.influencer_id || r.id;
            const updatedId = updatedDetails.influencer_id || updatedDetails.id;
            if (rId === updatedId) {
              // Return updated details, preserving any additional fields
              return { ...updatedDetails };
            }
            return r; // Keep other referrals unchanged
          })
        );

        // If this referral is currently selected in details view, update it too
        if (selectedInfluencer) {
          const selectedId = selectedInfluencer.influencer_id || selectedInfluencer.id;
          const updatedId = updatedDetails.influencer_id || updatedDetails.id;
          if (selectedId === updatedId) {
            setSelectedInfluencer({ ...updatedDetails });
          }
        }
      }
    } catch (err) {
      console.error('Error refreshing individual referral:', err);
      // Fallback to full reload if individual refresh fails
      loadData();
    }
  }, [selectedInfluencer, loadData]);

  useEffect(() => {
    if (!showOverlay) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') handleCloseDetails();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [showOverlay, handleCloseDetails]);

  useEffect(() => {
    if (!showCreateOverlay) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') handleCloseNewReferral();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [showCreateOverlay, handleCloseNewReferral]);

  return (
    <div className="referral-tracker-page">
      <header className="referral-tracker-header">
        <h4 className={textStyle(h4, 'text-gray-900')}>
          Hyper-Local Influencer Referral Tracker
        </h4>
        <div className="flex items-center gap-3">
          <IconTextButton
            icon={<Ticket className="w-5 h-5" />}
            text="Apply referral Code"
            onClick={() => {
              // Open coupon overlay - user will need to select which referral to use
              // For now, we'll open it without a specific referral and let them select
              setSelectedReferralForCoupon(null);
              setShowCouponOverlay(true);
            }}
            size="md"
          />
          <IconTextButton
            icon={<Plus className="w-5 h-5" />}
            text="Create Referral"
            onClick={() => {
              setEditingInfluencer(null);
              setShowNewReferralOverlay(true);
            }}
            size="md"
          />
        </div>
      </header>

      {/* {!loading && (
        <div className="referral-tracker-stats">
          <Card variant="light" className="referral-tracker-stat-card">
            <div className="referral-tracker-stat">
              <Users className="referral-tracker-stat-icon" />
              <span className={textStyle(p, 'referral-tracker-stat-value font-figtree font-bold')}>
                {stats.activeInfluencers}
              </span>
              <span className={textStyle(p, 'referral-tracker-stat-label')}>Active influencers</span>
            </div>
          </Card>
          <Card variant="light" className="referral-tracker-stat-card">
            <span className={textStyle(p, 'referral-tracker-stat-value font-figtree font-bold')}>
              {stats.totalReferrals}
            </span>
            <span className={textStyle(p, 'referral-tracker-stat-label')}>Total referrals</span>
          </Card>
          <Card variant="light" className="referral-tracker-stat-card">
            <span className={textStyle(p, 'referral-tracker-stat-value font-figtree font-bold')}>
              {stats.totalConversions}
            </span>
            <span className={textStyle(p, 'referral-tracker-stat-label')}>Conversions</span>
          </Card>
        </div>
      )} */}

      <div className="referral-tracker-layout">
        <ReferralList
          referrals={referrals}
          onSelectInfluencer={handleSelectInfluencer}
          loading={loading}
        />
      </div>

      {showOverlay &&
        createPortal(
          <AnimatePresence>
            <motion.div
              className="referral-tracker-overlay-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleCloseDetails}
              role="button"
              tabIndex={0}
              aria-label="Close overlay"
              onKeyDown={(e) => e.key === 'Escape' && handleCloseDetails()}
            >
              <motion.div
                className="referral-tracker-overlay-panel"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Card.NoShadowGradient className="referral-tracker-overlay-card">
                  {detailsLoading ? (
                    <div className="referral-tracker-loading referral-tracker-overlay-loading">
                      <div className="referral-tracker-spinner" />
                      <p className={textStyle(p, 'text-gray-600 mt-4')}>Loading details...</p>
                    </div>
                  ) : (
                    <ReferralDetails
                      influencer={selectedInfluencer}
                      onClose={handleCloseDetails}
                      onDelete={() => loadData()}
                      onEdit={handleEditInfluencer}
                    />
                  )}
                </Card.NoShadowGradient>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}

      {showCreateOverlay &&
        createPortal(
          <AnimatePresence>
            <motion.div
              className="referral-tracker-overlay-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleCloseNewReferral}
              role="button"
              tabIndex={0}
              aria-label="Close overlay"
              onKeyDown={(e) => e.key === 'Escape' && handleCloseNewReferral()}
            >
              <motion.div
                className="referral-tracker-overlay-panel"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Card.NoShadowGradient className="referral-tracker-overlay-card">
                  <NewReferralOverlay
                    onClose={handleCloseNewReferral}
                    onSuccess={handleNewReferralSuccess}
                    influencer={editingInfluencer}
                  />
                </Card.NoShadowGradient>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}

      {showCouponOverlay &&
        createPortal(
          <AnimatePresence>
            <motion.div
              className="referral-tracker-overlay-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleCloseCouponOverlay}
              role="button"
              tabIndex={0}
              aria-label="Close overlay"
              onKeyDown={(e) => e.key === 'Escape' && handleCloseCouponOverlay()}
            >
              <motion.div
                className="referral-tracker-overlay-panel"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Card.NoShadowGradient className="referral-tracker-overlay-card">
                  <ApplyCouponOverlay
                    referral={selectedReferralForCoupon}
                    referrals={referrals}
                    onClose={handleCloseCouponOverlay}
                    onApply={handleCouponApply}
                    onCodeCreated={handleCodeCreated}
                  />
                </Card.NoShadowGradient>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default HyperLocalReferralTracker;
